/**
 * Payments report exporters (CSV + PDF).
 *
 * Consumes the response shape of GET /api/admin/metrics/payments-report:
 *   { from_date, to_date, rows: [...], totals: {...} }
 */
import jsPDF from "jspdf";
import "jspdf-autotable";

const STATUS_LABEL = {
  pendiente: "Pendiente",
  en_produccion: "En Producción",
  listo: "Listo",
  enviado: "Enviado",
  entregado: "Entregado",
  cancelado: "Cancelado",
};

const VIA_LABEL = {
  transferencia: "Transferencia",
  efectivo: "Efectivo",
  stripe: "Tarjeta",
  otro: "Otro",
};

const fmtCurrency = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const rangeLabel = (from, to) => (from === to ? fmtDate(from) : `${fmtDate(from)} – ${fmtDate(to)}`);

/** Trigger a browser download for a text blob. */
function downloadBlob(content, filename, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export the payments report to CSV (UTF-8 with BOM so Excel opens accents correctly).
 */
export function exportPaymentsCsv(report, brand = "Printec") {
  const { from_date, to_date, rows, totals } = report;
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    "Pedido",
    "Cliente",
    "Teléfono",
    "Estado",
    "Fecha del pedido",
    "Anticipo pagado",
    "Fecha anticipo",
    "Vía anticipo",
    "Finiquito pagado",
    "Fecha finiquito",
    "Vía finiquito",
    "Total del pedido",
    "Ingresado en rango",
  ];
  const lines = [header.map(escape).join(",")];
  for (const r of rows) {
    lines.push([
      r.order_id,
      r.customer_name,
      r.customer_phone,
      STATUS_LABEL[r.status] || r.status,
      fmtDate(r.created_at),
      r.deposit_in_range ? r.deposit_amount.toFixed(2) : "",
      fmtDate(r.deposit_paid_at),
      VIA_LABEL[r.deposit_via] || r.deposit_via || "",
      r.final_in_range ? r.final_amount.toFixed(2) : "",
      fmtDate(r.final_paid_at),
      VIA_LABEL[r.final_via] || r.final_via || "",
      r.total.toFixed(2),
      r.received_in_range.toFixed(2),
    ].map(escape).join(","));
  }
  // Totals footer
  lines.push("");
  lines.push([`TOTALES ${rangeLabel(from_date, to_date)}`].map(escape).join(","));
  lines.push([`Pedidos con actividad`, totals.orders].map(escape).join(","));
  lines.push([`Anticipos cobrados`, totals.deposit_count, totals.deposit_amount.toFixed(2)].map(escape).join(","));
  lines.push([`Finiquitos cobrados`, totals.final_count, totals.final_amount.toFixed(2)].map(escape).join(","));
  lines.push([`Total ingresado`, "", totals.total_received.toFixed(2)].map(escape).join(","));

  const bom = "\uFEFF"; // makes Excel detect UTF-8
  const filename = `${brand.toLowerCase().replace(/\s+/g, "-")}-cobros-${from_date}_${to_date}.csv`;
  downloadBlob(bom + lines.join("\n"), filename);
}

/**
 * Export the payments report to a compact PDF table using jspdf-autotable.
 */
export function exportPaymentsPdf(report, settings = {}) {
  const { from_date, to_date, rows, totals } = report;
  const brand = settings.brand_name || "Printec";
  const company = settings.company_name || brand;
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginL = 12;
  const marginR = 12;

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(`${brand.toUpperCase()} — Reporte de cobros`, marginL, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text(company, marginL, 19);
  doc.setTextColor(20, 20, 20);
  doc.setFontSize(10);
  doc.text(`Rango: ${rangeLabel(from_date, to_date)}`, marginL, 25);

  // Summary block on the right side of the header
  const summaryX = pageWidth - marginR;
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text("Total ingresado", summaryX, 14, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(16, 129, 87);
  doc.text(fmtCurrency(totals.total_received), summaryX, 22, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text(
    `Anticipos ${totals.deposit_count} · ${fmtCurrency(totals.deposit_amount)}   ·   Finiquitos ${totals.final_count} · ${fmtCurrency(totals.final_amount)}`,
    summaryX,
    27,
    { align: "right" },
  );

  // Table
  const head = [[
    "Pedido",
    "Cliente",
    "Teléfono",
    "Estado",
    "Fecha pedido",
    "Anticipo",
    "Vía",
    "Finiquito",
    "Vía",
    "Total",
    "Ingresado",
  ]];
  const body = rows.map((r) => [
    (r.order_id || "").replace(/^o_/, ""),
    r.customer_name || "—",
    r.customer_phone || "",
    STATUS_LABEL[r.status] || r.status || "",
    fmtDate(r.created_at),
    r.deposit_in_range ? `${fmtCurrency(r.deposit_amount)}\n${fmtDate(r.deposit_paid_at)}` : "—",
    r.deposit_in_range ? (VIA_LABEL[r.deposit_via] || r.deposit_via || "") : "",
    r.final_in_range ? `${fmtCurrency(r.final_amount)}\n${fmtDate(r.final_paid_at)}` : "—",
    r.final_in_range ? (VIA_LABEL[r.final_via] || r.final_via || "") : "",
    fmtCurrency(r.total),
    fmtCurrency(r.received_in_range),
  ]);

  doc.autoTable({
    startY: 32,
    head,
    body,
    theme: "grid",
    styles: { font: "helvetica", fontSize: 8, cellPadding: 1.5, valign: "top" },
    headStyles: { fillColor: [15, 15, 15], textColor: 255, fontSize: 8, halign: "left" },
    alternateRowStyles: { fillColor: [246, 246, 246] },
    columnStyles: {
      0: { cellWidth: 22 },        // order id
      1: { cellWidth: 40 },        // name
      2: { cellWidth: 24 },        // phone
      3: { cellWidth: 22 },        // status
      4: { cellWidth: 20 },        // created_at
      5: { cellWidth: 24, halign: "right" }, // deposit
      6: { cellWidth: 18 },        // via
      7: { cellWidth: 24, halign: "right" }, // final
      8: { cellWidth: 18 },        // via
      9: { cellWidth: 22, halign: "right" }, // total
     10: { cellWidth: 24, halign: "right", fontStyle: "bold", textColor: [16, 129, 87] }, // received
    },
    didDrawPage: (data) => {
      // Footer with page number
      const str = `Página ${doc.internal.getNumberOfPages()}`;
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(str, pageWidth - marginR, doc.internal.pageSize.getHeight() - 6, { align: "right" });
      doc.text(new Date().toLocaleString("es-MX"), marginL, doc.internal.pageSize.getHeight() - 6);
    },
  });

  // Totals row after table
  const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 40;
  doc.setDrawColor(15, 15, 15);
  doc.setLineWidth(0.4);
  doc.line(marginL, finalY + 4, pageWidth - marginR, finalY + 4);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text(`Total ingresado en el rango:`, marginL, finalY + 10);
  doc.setTextColor(16, 129, 87);
  doc.setFontSize(12);
  doc.text(fmtCurrency(totals.total_received), pageWidth - marginR, finalY + 10, { align: "right" });

  const filename = `${brand.toLowerCase().replace(/\s+/g, "-")}-cobros-${from_date}_${to_date}.pdf`;
  doc.save(filename);
}
